import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  $normalizeSelection__EXPERIMENTAL,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  KEY_ENTER_COMMAND,
  type EditorState,
  type LexicalEditor,
  type LexicalNode,
  type TextNode,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createMentionNode, MentionNode } from './MentionNode';
import { MentionList } from './MentionList';
import {
  escapeRegExp,
  labelCharClass,
  mentionPadding,
  segmentMentions,
  type ComposerMentionItem,
  type ComposerMentions,
  type MentionMatcher,
} from './mention';
import {
  dropHint as dropHintCls,
  dropTarget as dropTargetCls,
  editable as editableCls,
  editorWrap,
  paragraph as paragraphCls,
  placeholder as placeholderCls,
} from './mention.css';

export interface MentionEditorProps {
  /** Current text (mentions serialised inline as `${trigger}Label`). */
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter / Cmd·Ctrl+Enter when sending is allowed. */
  onSend: (value: string) => void;
  /**
   * Explicit send override (e.g. over-length, in-flight). When omitted, sending
   * is gated on the editor's LIVE emptiness — so a just-pasted/typed value sends
   * even before the parent re-renders the controlled `value`.
   */
  sendDisabled?: boolean;
  placeholder: string;
  /** Accessible name for the editable. */
  ariaLabel: string;
  /** Classes for the editable surface (shared textArea + prominence classes). */
  editableClassName: string;
  /** Mention typeahead config(s), or undefined to disable them. */
  mentions?: ComposerMentions | readonly ComposerMentions[];
  /** Populated with the send action, so the out-of-editor send button reads live content. */
  sendRef: React.MutableRefObject<() => void>;
}

/** `Array.isArray` alone widens a readonly array to `any[]`; this keeps the type. */
function isConfigList(
  m: ComposerMentions | readonly ComposerMentions[],
): m is readonly ComposerMentions[] {
  return Array.isArray(m);
}

/** Builds an editor's node list from a plain string (mentions → atomic chips). */
function $buildNodes(value: string, matchers: readonly MentionMatcher[]) {
  const nodes = [];
  for (const seg of segmentMentions(value, matchers)) {
    if (seg.mention) {
      nodes.push($createMentionNode(seg.text));
      continue;
    }
    const lines = seg.text.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) nodes.push($createLineBreakNode());
      if (line) nodes.push($createTextNode(line));
    });
  }
  return nodes;
}

/** Reads the editor's plain-text value (mention chips serialise to their text). */
function readValue(editor: LexicalEditor): string {
  return editor.getEditorState().read(() => $getRoot().getTextContent());
}

/** Reflects an external `value` into the editor, without clobbering local edits. */
function ValueSyncPlugin({
  value,
  matchers,
}: {
  value: string;
  matchers: readonly MentionMatcher[];
}) {
  const [editor] = useLexicalComposerContext();
  const matchersRef = useRef(matchers);
  matchersRef.current = matchers;

  useEffect(() => {
    if (readValue(editor) === value) return; // our own edit round-tripped — skip
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const para = $createParagraphNode();
      para.append(...$buildNodes(value, matchersRef.current));
      root.append(para);
      para.selectEnd();
    });
  }, [editor, value]);
  return null;
}

/** Enter / Cmd·Ctrl+Enter send; Shift+Enter inserts a newline. */
function SendPlugin({
  onSend,
  sendDisabled,
  sendRef,
}: {
  onSend: (value: string) => void;
  sendDisabled?: boolean;
  /** Populated with the send action so the (out-of-editor) send button can call it. */
  sendRef: React.MutableRefObject<() => void>;
}) {
  const [editor] = useLexicalComposerContext();
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  const disabledRef = useRef(sendDisabled);
  disabledRef.current = sendDisabled;

  // Both the send button and Enter route through this ONE live read of the
  // editor, so they can never diverge on a stale value — and, with no explicit
  // override, emptiness is judged from the LIVE editor content (not the parent's
  // lagging controlled `value`), so a just-entered value sends immediately.
  const doSend = useCallback(() => {
    const text = readValue(editor);
    const disabled = disabledRef.current ?? text.trim().length === 0;
    if (!disabled) onSendRef.current(text);
  }, [editor]);

  useEffect(() => {
    sendRef.current = doSend;
  }, [sendRef, doSend]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          // Shift+Enter → newline (let the default plain-text handler run).
          if (event?.shiftKey) return false;
          // Plain Enter or Cmd/Ctrl+Enter → send (never insert a newline).
          event?.preventDefault();
          doSend();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, doSend],
  );
  return null;
}

/**
 * The DOM caret at a point, across the two spellings browsers ship. Returns
 * `null` where neither exists — notably jsdom/happy-dom, which is why the drop
 * below degrades to "append at the end" rather than failing.
 */
function caretFromPoint(x: number, y: number): { node: Node; offset: number } | null {
  // Called through `doc.` rather than extracted into a variable — an unbound
  // DOM method loses its receiver (and trips `no-unbound-method`).
  const doc = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  // Chromium/WebKit.
  if (typeof doc.caretRangeFromPoint === 'function') {
    const range = doc.caretRangeFromPoint(x, y);
    return range && { node: range.startContainer, offset: range.startOffset };
  }
  // Firefox / the standardised name.
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    return pos && { node: pos.offsetNode, offset: pos.offset };
  }
  return null;
}

/** The Lexical selection at a drop point, or `null` when none can be resolved. */
function selectionAtPoint(x: number, y: number) {
  const point = caretFromPoint(x, y);
  if (!point) return null;
  const node = $getNearestNodeFromDOMNode(point.node);
  if (!node) return null;
  const selection = $createRangeSelection();
  if ($isTextNode(node)) {
    selection.anchor.set(node.getKey(), point.offset, 'text');
    selection.focus.set(node.getKey(), point.offset, 'text');
  } else {
    // A non-text node (a chip, the paragraph) — land AFTER it.
    //
    // `getParent()`, never `getParentOrThrow()`: a caret resolving to the
    // contenteditable ITSELF yields the RootNode, which has no parent. That
    // throw would land inside `editor.update()` AFTER `preventDefault()`, so
    // the native drop is suppressed too and the gesture silently does nothing
    // — the worst shape of failure for a drag. Not reproduced in Chromium (the
    // caret resolves to the paragraph or a text node at every point tested,
    // corners and padding included), but the path is real and the guard is
    // free: fall through to the documented end-of-message fallback.
    const parent = node.getParent();
    if (!parent) return null;
    const index = node.getIndexWithinParent() + 1;
    selection.anchor.set(parent.getKey(), index, 'element');
    selection.focus.set(parent.getKey(), index, 'element');
  }
  return $normalizeSelection__EXPERIMENTAL(selection);
}

/**
 * The text immediately either side of the current selection — just enough for
 * {@link mentionPadding} to decide on spaces. Only the nearest character
 * matters, so one node's worth (falling back to a sibling when the anchor sits
 * on a boundary) is all this reads.
 */
function textAroundSelection(anchorNode: LexicalNode, offset: number): [string, string] {
  // An ELEMENT anchor points at a child INDEX, so the neighbours are that
  // element's children either side of `offset` — NOT the element's own
  // siblings. Reading siblings here returned '' for `before`, which reads as
  // "start of message", so no leading space was added and the token glued onto
  // the node in front of it (`/plan.md/notes.md`). No longer word-initial, so
  // `segmentMentions` never rebuilds it as a chip and the agent receives one
  // unresolvable token.
  if ($isElementNode(anchorNode)) {
    return [
      anchorNode.getChildAtIndex(offset - 1)?.getTextContent() ?? '',
      anchorNode.getChildAtIndex(offset)?.getTextContent() ?? '',
    ];
  }
  const own = anchorNode.getTextContent();
  let before = own.slice(0, offset);
  let after = own.slice(offset);
  // A text anchor sitting on a boundary — the neighbouring text is the sibling.
  if (before === '') before = anchorNode.getPreviousSibling()?.getTextContent() ?? '';
  if (after === '') after = anchorNode.getNextSibling()?.getTextContent() ?? '';
  return [before, after];
}

/**
 * Dropping a mention token (`/plan.md` dragged off a file card, `@Architect`
 * off a roster row) commits it as a real chip instead of the raw text a
 * contentEditable would otherwise take.
 *
 * Registered ABOVE Lexical's own `DROP_COMMAND` handler (which is
 * `COMMAND_PRIORITY_EDITOR`, the lowest) and returns `true` only for a payload
 * that is entirely ONE known mention — so every other drop still gets the
 * default plain-text behaviour untouched.
 *
 * An INTERNAL drag is declined outright. Dragging a committed chip to a new
 * spot in the same message puts that chip's own text on the `dataTransfer`, so
 * it matches this handler perfectly: it would `preventDefault()` and append a
 * SECOND copy at the end, while Lexical's `$handlePlainTextDrop` — the half
 * that removes the original — never ran. `look at /plan.md now` dragged one
 * word left became `look at /plan.md now /plan.md`. An internal drag is a MOVE
 * and Lexical already handles it correctly; this plugin exists for text
 * arriving from OUTSIDE the editor.
 *
 * The chip lands AT THE DROP POINT. It used to append at the end of the
 * message regardless of where you dropped, which made the same reference read
 * differently depending on how you made it — drag bolted a token onto the end
 * while the `/` typeahead put one exactly where you were typing. Where no
 * caret can be resolved (jsdom, and any browser shipping neither spelling of
 * `caretFromPoint`) it still degrades to the end of the message.
 */
function MentionDropPlugin({ matchers }: { matchers: readonly MentionMatcher[] }) {
  const [editor] = useLexicalComposerContext();
  const matchersRef = useRef(matchers);
  matchersRef.current = matchers;
  /** Set while a drag that STARTED in this editor is in flight. */
  const internalDrag = useRef(false);

  useEffect(() => {
    const unregister = editor.registerCommand(
      DRAGSTART_COMMAND,
      () => {
        internalDrag.current = true;
        // Never handled here — this only observes, so Lexical's own dragstart
        // still runs and writes the payload.
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
    // `dragend` fires whether or not the drag ended in a drop, so a chip
    // dragged out of the editor and released elsewhere cannot leave the flag
    // stuck and make the NEXT external drop fall through to plain text.
    const clear = () => {
      internalDrag.current = false;
    };
    window.addEventListener('dragend', clear);
    return () => {
      unregister();
      window.removeEventListener('dragend', clear);
    };
  }, [editor]);

  useEffect(
    () =>
      editor.registerCommand(
        DROP_COMMAND,
        (event: DragEvent) => {
          if (internalDrag.current) {
            internalDrag.current = false;
            return false;
          }
          const dropped = event.dataTransfer?.getData('text/plain').trim() ?? '';
          const segments = segmentMentions(dropped, matchersRef.current);
          // Exactly one segment, and it is a mention: anything else (prose, a
          // half-match, several tokens) is not ours — let Lexical handle it.
          if (segments.length !== 1 || !segments[0]!.mention) return false;
          event.preventDefault();
          editor.update(() => {
            const root = $getRoot();
            const droppedAt = selectionAtPoint(event.clientX, event.clientY);
            if (droppedAt) $setSelection(droppedAt);
            // No caret resolvable (jsdom; a browser with neither spelling) —
            // the end of the message is the honest fallback, not a failure.
            const selection = droppedAt ?? root.selectEnd();
            const [before, after] = textAroundSelection(
              selection.anchor.getNode(),
              selection.anchor.offset,
            );
            // A mention is word-initial, so a token landing straight after a
            // word rebuilds as plain text and the chip silently disappears.
            const pad = mentionPadding(before, after);
            const nodes = [];
            if (pad.leading) nodes.push($createTextNode(' '));
            nodes.push($createMentionNode(dropped));
            if (pad.trailing) nodes.push($createTextNode(' '));
            selection.insertNodes(nodes);
          });
          // Drag-and-drop leaves focus on the drag source; the user's next
          // action is typing the rest of the message.
          editor.focus();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );
  return null;
}

/** One agent row in the typeahead. */
class MentionOption extends MenuOption {
  item: ComposerMentionItem;
  constructor(item: ComposerMentionItem) {
    super(item.id);
    this.item = item;
  }
}

/**
 * Live trigger match for one trigger — the open-the-menu half of the same rule
 * `segmentMentions` uses to commit a chip, built from the SAME
 * {@link labelCharClass} so a query can never run past where the chip would end.
 *
 * This replaces Lexical's `useBasicTypeaheadTriggerMatch`, which defines a query
 * by EXCLUSION (`[^<trigger><PUNCTUATION>\s]`) over a punctuation set that
 * contains `.`, `-` and `_`. That is right for single-word agent labels and
 * wrong for anything else: with a `/` trigger, `/q3-report.md` matches a query
 * of just `q3`, so the list stops filtering at the first hyphen.
 */
function useTriggerFn(trigger: string, labelChars: string | undefined) {
  return useMemo(() => {
    // Same word-initial rule as segmentMentions; anchored at the caret.
    const re = new RegExp(
      `(^|\\s)(${escapeRegExp(trigger)}([${labelCharClass(labelChars)}]{0,75}))$`,
      'u',
    );
    return (text: string) => {
      const m = re.exec(text);
      if (m === null) return null;
      return {
        leadOffset: m.index + m[1]!.length,
        matchingString: m[3]!,
        replaceableString: m[2]!,
      };
    };
  }, [trigger, labelChars]);
}

/** One trigger's typeahead: shows the item list, inserts an atomic chip on select. */
function MentionsPlugin({ mentions }: { mentions: ComposerMentions }) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const trigger = mentions.trigger ?? '@';
  const triggerFn = useTriggerFn(trigger, mentions.labelChars);

  const items = useMemo(() => (query != null ? mentions.getItems(query) : []), [query, mentions]);
  const options = useMemo(() => items.map((it) => new MentionOption(it)), [items]);

  const onSelectOption = useCallback(
    (option: MentionOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const chip = $createMentionNode(`${trigger}${option.item.label}`);
        if (nodeToReplace) nodeToReplace.replace(chip);
        const space = $createTextNode(' ');
        chip.insertAfter(space);
        space.select();
        closeMenu();
      });
      mentions.onSelect?.(option.item);
    },
    [editor, trigger, mentions],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      options={options}
      // Beat the send handler (COMMAND_PRIORITY_LOW): while the menu is open,
      // Enter/Tab must select the highlighted agent, not submit the message.
      commandPriority={COMMAND_PRIORITY_NORMAL}
      menuRenderFn={(anchorRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorRef.current && options.length > 0
          ? createPortal(
              <MentionList
                items={items}
                activeIndex={selectedIndex ?? 0}
                query={query ?? ''}
                listLabel={mentions.listLabel ?? 'Suggestions'}
                onHover={setHighlightedIndex}
                onSelect={(i) => {
                  setHighlightedIndex(i);
                  selectOptionAndCleanUp(options[i]!);
                }}
              />,
              anchorRef.current,
            )
          : null
      }
    />
  );
}

/**
 * MentionEditor — the Composer's text field, a Lexical plain-text editor. Same
 * behaviour as the old textarea (Enter sends, Shift+Enter newlines, Cmd/Ctrl+
 * Enter sends, empty = no send, auto-grows then scrolls), plus one typeahead
 * per configured trigger — each inserting real, atomic, styled chips, from the
 * menu or from a drop. Controlled via `value` / `onChange`; the value is plain
 * text with mentions serialised inline.
 */
export function MentionEditor({
  value,
  onChange,
  onSend,
  sendDisabled,
  placeholder,
  ariaLabel,
  editableClassName,
  mentions,
  sendRef,
}: MentionEditorProps) {
  // One config or several — normalised once so everything below sees a list.
  const configs = useMemo<readonly ComposerMentions[]>(
    () => (mentions == null ? [] : isConfigList(mentions) ? mentions : [mentions]),
    [mentions],
  );
  // What each trigger considers a committed mention: a label the consumer's own
  // `getItems` still returns for that exact word.
  const matchers = useMemo<MentionMatcher[]>(
    () =>
      configs.map((config) => ({
        trigger: config.trigger ?? '@',
        labelChars: config.labelChars,
        isMention: (word: string) =>
          config.getItems(word).some((i) => i.label.toLowerCase() === word.toLowerCase()),
      })),
    [configs],
  );
  // Seeds the initial editor state ONCE (see initialConfig) — a ref so that
  // read isn't a lie about the dependency.
  const initialMatchers = useRef(matchers);

  const initialConfig = useMemo(
    () => ({
      namespace: 'composer',
      nodes: [MentionNode],
      theme: { paragraph: paragraphCls },
      onError: (error: Error) => {
        throw error;
      },
      editorState: () => {
        const root = $getRoot();
        if (root.getFirstChild() !== null) return;
        const para = $createParagraphNode();
        para.append(...$buildNodes(value, initialMatchers.current));
        root.append(para);
      },
    }),
    // Config is created once; `value` seeds only the initial state, and
    // ValueSyncPlugin owns updates thereafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Drag-hover state for the drop affordance above. `dragDepth` counts
  // enter/leave pairs across descendants; `internalDrag` marks a drag that
  // began inside this editor (a chip being moved), which gets no hint.
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const internalDrag = useRef(false);
  const endDrag = useCallback(() => {
    dragDepth.current = 0;
    internalDrag.current = false;
    setDragOver(false);
  }, []);

  const lastEmitted = useRef(value);
  const handleChange = useCallback(
    (editorState: EditorState) => {
      const text = editorState.read(() => $getRoot().getTextContent());
      if (text === lastEmitted.current) return;
      lastEmitted.current = text;
      onChange(text);
    },
    [onChange],
  );

  return (
    <div
      className={dragOver ? `${editorWrap} ${dropTargetCls}` : editorWrap}
      // Drag feedback lives on the WRAPPER, not on a Lexical command: the class
      // belongs to this element, and Lexical's own DRAGOVER handling stays
      // untouched. `dragenter`/`dragleave` fire per descendant as the pointer
      // crosses the placeholder, the paragraph and every chip, so a boolean
      // flickers — the depth counter is what makes it steady.
      onDragStart={() => {
        // A drag that STARTED here is a move, not an add. Suppressing the hint
        // matches MentionDropPlugin, which declines the drop for the same
        // reason.
        internalDrag.current = true;
      }}
      onDragEnter={(e) => {
        if (internalDrag.current || !e.dataTransfer.types.includes('text/plain')) return;
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragLeave={() => {
        if (dragDepth.current === 0) return;
        dragDepth.current -= 1;
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={endDrag}
      onDragEnd={endDrag}
    >
      {dragOver && (
        // aria-hidden: transient pointer-gesture chrome. A drag is already
        // pointer-only, so there is no screen-reader user mid-gesture to
        // announce this to.
        <div className={dropHintCls} aria-hidden="true">
          Drop to add
        </div>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className={`${editableClassName} ${editableCls}`}
              ariaLabel={ariaLabel}
              aria-placeholder={placeholder}
              placeholder={<div className={placeholderCls}>{placeholder}</div>}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        <ValueSyncPlugin value={value} matchers={matchers} />
        <SendPlugin onSend={onSend} sendDisabled={sendDisabled} sendRef={sendRef} />
        {configs.map((config) => (
          <MentionsPlugin key={config.trigger ?? '@'} mentions={config} />
        ))}
        {matchers.length > 0 && <MentionDropPlugin matchers={matchers} />}
      </LexicalComposer>
    </div>
  );
}
MentionEditor.displayName = 'MentionEditor';
